import { CupisWorkTools } from './CupisWorkTools';
import { PRIVATE_MIRROR, RU_FEED } from '../lib/feedContext';
import { ZeroHint } from './ZeroGuide';
import { isPilot } from './dry';
/**
 * Привычный калькулятор вилки: те же замки, тот же плюс, что у карточки.
 *
 * Второго счёта нет — числа берёт `rowWithLock`. Ввод пишет в `oddsLock` /
 * `stakeLock` / валюту / комиссию / фрибет. «P» и «ф» на экране нет.
 *
 * Три поверхности, одна арифметика.
 * `desk` — стол сверху ленты, как у Форкса / Positivebet: контора, исход,
 * кэф с галочкой «живой», сумма, доход. На бирже сумма — баксы или шеры:
 * клик по знаку рядом с числом. Вбил 2$ на полике — тандер едет за живым
 * кэфом. Вбил шеры — баксы едут за ценой. В купон «скопировать» кладёт шеры.
 * Вбил одно плечо — второе само. На столе кэф и сумма — с тонкой чертой
 * снизу, чтобы было видно, что они правятся (владелец, 02.10); копировать —
 * значок, фрибет — билет FREE, call — золотой колокольчик после «в кассу».
 * Снял галочку или вписал кэф — крутишь сам, глядя в контору. «округлить»
 * режет вторую сумму до 10 / 100 / 1000. `pad` — те же числа картами под
 * карточкой, живой кэф — этой ноги с ленты; страница только если сверка узнала
 * ту же ставку, не соседнюю полку. Шапка пада: выйдет, всего, «в окошко»,
 * «ещё плечо», «разложить банк». Белая заливка на сумме запрещена: на кадре уже «открыть обе».
 * Кэф и сумма — число как на плитке: без колодца и без черты карандаша.
 * Выбранное — светлая линия сверху. Валюта — один знак после суммы.
 * Копировать — слово в ряду ручек. Контора — та же `Brand`, что на плитке.
 * `bar` — две реплики в узком окне:
 * «кэф на сумму», плюс и всего справа. Клик по «кэф»/«%» — тот же кэф,
 * другим числом (100/к). Лимит двух ног и общее «всего» — снова `stakes()`,
 * не второй счёт. Вынести — Document Picture-in-Picture, где браузер умеет;
 * иначе обычный попап: адресную строку страница спрятать не может.
 * Вилка ушла или ушла в минус — окно держит последние числа, само не закрывается.
 *
 * Банк сита отсюда не трогаем. Поле суммы — раскладка ЭТОЙ вилки: якорь через
 * `stakes()` и замок первой денежной ноги. Иначе 1000 в виджете обрушило бы ленту.
 */

import { useEffect, useState } from 'react';
import { CaptureButton } from '../book/cash/CaptureButton';
import { rememberHref } from './pickHref';

import { Decimal } from 'decimal.js';

import { placeMark } from '../lib/align';
import { toggleClip, useClips } from './clip';
import { impliedSum, stakes } from '../lib/calc';
import { isShareLeg, isShareVenue, lookLikeShareCents, oddsFromShareLock } from '../lib/shareVenue';
import { BLANK_CALC_ID, HAND_SOURCE, blankCalcFork, buildRow, centsText, type ExchangeStake, type RowLeg } from '../lib/row';
import { driveKey } from '../confirm/drive';
import { useDrive } from './driveNote';
import { liveHoldKey, paintLiveAsks, useLiveBookTick } from './liveBook';
import { bornRoiOf, pageAgeMs, pageBy, pageShut, pageSite, paintLive, rememberBornRoi, usePageTick } from './liveQuote';
import { isPolymarket } from './order';
import { useLivePolyBook } from './useLivePolyBook';
import { sightTitle, sightTone } from './sightLight';
import { ROUND_STEPS, isRoundStep, tempoOf, useSettings, type RoundStep } from '../store/settings';
import * as act from './act';
import { AmountEdit } from './AmountEdit';
import { Bell } from './Bell';
import { Brand } from './Brand';
import { CurEdit } from './CurEdit';
import { ShareUnitEdit } from './ShareUnitEdit';
import { FeeEdit } from './FeeEdit';
import { FreeEdit } from './FreeEdit';
import { copy } from './Grab';
import type { Meta } from './meta';
import { venueKind, venueName } from './meta';
import { OddsEdit } from './OddsEdit';
import { canPip } from './pip';
import { betOn } from './pick';
import { rowWithLock, useHiddenRows, useRows, type FeedRow } from './rows';
import {
  catalogCur,
  convertStakeForm,
  editAmount,
  forForm as formOf,
  hasRubBook,
  isRubStake,
  lockAsOf,
  lockForm,
  profitText,
  stakeLockBody,
  useShow,
} from './show';
import { rub, usd } from './format';
import { useEntry } from './entry';
import { useScreen } from './store';
import { useSession } from '../access/session';
import { useDeskCall } from './deskCall';
import { CalcFork } from './CalcFork';
import { useDeskLayout } from './deskLayout';
import { holdLegs } from './order';

function moneyAnchor(legs: readonly RowLeg[]): RowLeg | undefined {
  return (
    legs.find((leg) => leg.unit === 'money' && leg.source !== HAND_SOURCE) ??
    legs.find((leg) => leg.unit === 'money') ??
    legs[0]
  );
}

function shownTotal(staked: number, fx: number, asUsd: boolean): string {
  if (!asUsd || !(fx > 0)) return String(Math.round(staked));
  return (staked / fx).toFixed(2);
}

function parseAmount(value: string): number | null {
  const raw = value.trim().replace(/\s/g, '').replace(',', '.');
  if (raw === '' || raw === '.') return null;
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Доля рынка как процент: 2.000 → 50.00. Обратное — тот же кэф, не новый счёт. */
export function impliedOf(odds: string): string {
  try {
    const n = new Decimal(odds.trim().replace(',', '.'));
    if (!n.gt(1)) return '';
    return new Decimal(100).div(n).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toFixed(2);
  } catch {
    return '';
  }
}

export function oddsFromImplied(pct: string): string | null {
  try {
    const n = new Decimal(pct.trim().replace(',', '.'));
    if (!n.gt(0) || !n.lt(100)) return null;
    const odds = new Decimal(100).div(n);
    if (!odds.gt(1)) return null;
    return odds.toDecimalPlaces(3, Decimal.ROUND_DOWN).toFixed(3);
  } catch {
    return null;
  }
}

const heldCalc = new Map<string, FeedRow>();

export function useHeldCalcRow(id: string): { row: FeedRow | null; gone: boolean; reconnecting: boolean } {
  const forks = useScreen((s) => s.forks);
  const ghosts = useScreen((s) => s.ghosts);
  const hiddenForks = useScreen((s) => s.hiddenForks);
  const status = useScreen((s) => s.status);
  const framed = useScreen((s) => s.framed);
  const { big, tail } = useRows();
  const hidden = useHiddenRows();
  const bank = useSettings((s) => s.bank);
  const fx = useSettings((s) => s.fx);
  const caps = useSettings((s) => s.caps);
  const capsCur = useSettings((s) => s.capsCur);
  const couponCaps = useSettings((s) => s.couponCaps);
  const freebets = useSettings((s) => s.freebets);
  const freebetsCur = useSettings((s) => s.freebetsCur);
  const attempts = useSettings((s) => s.attempts);
  const roundStep = useSettings((s) => s.roundStep);
  const roundDir = useSettings((s) => s.roundDir);
  const roundWho = useSettings((s) => s.roundWho);
  const stakeLock = useScreen((s) => s.stakeLock);
  const oddsLock = useScreen((s) => s.oddsLock);
  const feeLock = useScreen((s) => s.feeLock);
  const curLock = useScreen((s) => s.curLock);
  const freeLock = useScreen((s) => s.freeLock);
  const handLock = useScreen((s) => s.handLock);
  const unit = useShow((s) => s.unit);
  usePageTick();
  useLiveBookTick();
  const visible = [...big, ...tail, ...hidden].find((one) => one.id === id) ?? null;
  const ghost = ghosts.get(id) ?? null;
  const rawFork =
    id === BLANK_CALC_ID
      ? blankCalcFork(unit !== 'rub' && fx > 0 ? 'USD' : 'RUB')
      : forks.get(id) ??
        visible?.row.fork ??
        ghost?.fork ??
        hiddenForks.get(id)?.fork ??
        heldCalc.get(id)?.row.fork;
  const reconnecting = status !== 'live' || !framed;
  const gone = rawFork !== undefined && !forks.has(id) && !reconnecting && id !== BLANK_CALC_ID;
  const locked = new Set(
    oddsLock?.forkId === id ? Object.keys(oddsLock.by) : [],
  );
  const polyTape = rawFork?.legs?.find((leg) => isPolymarket(leg.s) && (leg.unit === 'share' || isShareLeg(leg)));
  useLivePolyBook(
    polyTape !== undefined
      ? {
          url: (PRIVATE_MIRROR || RU_FEED) ? (polyTape.betUrl ?? polyTape.url) : polyTape.url,
          asset: polyTape.asset ?? null,
          book: polyTape.book,
          marketKey: (PRIVATE_MIRROR || RU_FEED) ? (polyTape.market ?? rawFork?.market) : rawFork?.market,
          side: polyTape.side,
          name: polyTape.name ?? null,
          who: polyTape.participants,
        }
      : null,
  );
  const taped =
    rawFork === undefined
      ? undefined
      : paintLive(rawFork, forks.values(), pageBy(id), locked, undefined, pageSite(id));
  const fork = taped === undefined ? undefined : paintLiveAsks(taped, null, locked);
  let row: FeedRow | null = null;
  if (fork !== undefined) {
    const priced = buildRow(fork, {
      bank,
      fx,
      caps,
      capsCur,
      couponCaps,
      freebets,
      freebetsCur,
      tempo: tempoOf(attempts),
      roundStep,
      roundDir,
      roundWho,
      lock: stakeLock ?? undefined,
      odds: oddsLock ?? undefined,
      fees: feeLock ?? undefined,
      curs: curLock ?? undefined,
      free: freeLock ?? undefined,
      hand: handLock ?? undefined,
      hold: true,
    });
    if (priced !== null) {
      const sites = pageSite(id);
      const need = (fork.legs ?? []).filter((leg) => leg.s !== HAND_SOURCE);
      if (need.length > 0 && need.every((leg) => sites.has(leg.s))) {
        rememberBornRoi(id, String(priced.roiPct));
      }
      const prev = visible ?? heldCalc.get(id);
      row = {
        id,
        row: priced,
        age: visible?.age ?? prev?.age ?? 0,
        tier: visible?.tier ?? prev?.tier ?? 'small',
        born: visible?.born ?? false,
        beat: visible?.beat ?? 0,
        risky: visible?.risky ?? false,
        still: visible?.still ?? null,
        linkless: priced.legs.some((leg) => leg.url === null && leg.source !== HAND_SOURCE),
        lifespan: visible?.lifespan ?? prev?.lifespan ?? null,
        ghost:
          gone && !reconnecting
            ? (visible?.ghost ?? ghost ?? { fork, why: 'other', at: Date.now() })
            : null,
      };
    }
  }
  if (row !== null) heldCalc.set(id, row);
  else row = heldCalc.get(id) ?? null;
  return { row, gone, reconnecting };
}

/** Замок биржи хранит центы. В калькуляторе человек набивает кэф, как в конторе. */
function centsFromOdds(odds: string): string | null {
  try {
    const n = new Decimal(odds.trim().replace(',', '.'));
    if (!n.gt(1)) return null;
    const cents = new Decimal(100).div(n);
    return cents.isInteger() ? cents.toFixed(0) : cents.toDecimalPlaces(1).toString();
  } catch {
    return null;
  }
}

function shareQuote(leg: Pick<RowLeg, 'source' | 'unit'>): boolean {
  return leg.unit === 'share' || isShareVenue(leg.source);
}

function quoteOf(
  leg: RowLeg,
  forkId: string,
  oddsLock: { forkId: string; by: Record<string, string> } | null,
  handOdds: string | undefined,
  hasHand: boolean,
): { form: string; locked: boolean; cents: string | null } {
  const share = shareQuote(leg);
  if (leg.source === HAND_SOURCE) {
    return { form: handOdds ?? leg.odds, locked: hasHand, cents: null };
  }
  const locked = Boolean(oddsLock?.forkId === forkId && oddsLock.by[leg.source] !== undefined);
  const raw = locked ? oddsLock!.by[leg.source]! : null;
  const form = share
    ? raw !== null
      ? oddsFromShareLock(raw)
      : !PRIVATE_MIRROR && !RU_FEED && lookLikeShareCents(Number(leg.odds))
        ? oddsFromShareLock(leg.odds)
        : leg.odds
    : (raw ?? leg.odds);
  const cents = share ? centsText(leg.cents ?? 0) : null;
  return { form, locked, cents };
}

function incomeSay(income: number, asUsd: boolean, fx: number): string {
  if (asUsd && fx > 0) {
    return `${income < 0 ? '−' : '+'}${usd(Math.abs(income) / fx)} $`;
  }
  return `${income < 0 ? '−' : '+'}${rub(Math.abs(income))} ₽`;
}

/** Исторический интервал: окно больше не закрывается по таймеру. */
export const CALC_GONE_MS = 2000;

function CopyMark({ value }: { value: string }): React.ReactElement {
  return (
    <button
      type="button"
      className="nx-calc__copy"
      title={`скопировать ${value}`}
      aria-label={`скопировать ${value}`}
      onClick={() => void copy(value)}
    >
      <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
        <rect x="5.5" y="5.5" width="8" height="8" rx="1.5" />
        <path d="M10.5 3.5v-1h-8v8h1" />
      </svg>
    </button>
  );
}

/** Галочка Форкса: кэф едет с ленты, пока сам не снимешь. */
function LiveTick({
  on,
  onToggle,
  named = false,
}: {
  on: boolean;
  onToggle: () => void;
  named?: boolean;
}): React.ReactElement {
  return (
    <button
      type="button"
      className={`nx-calc__live${on ? ' on' : ''}${named ? ' named' : ''}`}
      title={
        on
          ? 'кэф этой ноги с ленты, с ячейки — если сверка узнала ту же ставку. сними — застынет, пишешь сам'
          : 'кэф застыл. поставь галочку — снова эта нога с ленты, не сосед и не рождение'
      }
      aria-pressed={on}
      aria-label={on ? 'живой кэф' : 'кэф застыл'}
      onClick={(event) => {
        event.stopPropagation();
        onToggle();
      }}
    >
      <i aria-hidden="true" />
      {named ? <em>{on ? 'живой' : 'застыл'}</em> : null}
    </button>
  );
}

function SightDot({
  forkId,
  source,
  url,
  nowRoiPct,
}: {
  forkId: string;
  source: string;
  url: string | null;
  nowRoiPct: number;
}): React.ReactElement | null {
  if (source === HAND_SOURCE) return null;
  const age = pageAgeMs(forkId, source);
  const drive = useDrive((s) => (url ? s.byIntent[driveKey(source, url)] : undefined));
  const fromPage = pageShut(forkId, source);
  const shut = fromPage || (age === null && drive?.suspended === true);
  const tone = sightTone({
    siteAgeMs: age,
    nowRoiPct,
    bornRoiPct: bornRoiOf(forkId),
    shut,
  });
  const title = sightTitle(tone, age);
  return (
    <>
      <i className={`nx-calc__sight nx-calc__sight--${tone}`} title={title} aria-label={title} />
      {shut ? (
        <em className="nx-calc__shut" title={title}>
          заблок
        </em>
      ) : null}
    </>
  );
}

const ROUND_TITLE: Record<RoundStep, string> = {
  1: 'вторая сумма до целых',
  5: 'вторая сумма кратна пяти',
  10: 'вторая сумма до десятков',
  100: 'вторая сумма до сотен',
  1000: 'вторая сумма до тысяч',
};

/** Шаг суммы, как выпадашка у Positivebet: чтобы в контору шла ровная. */
function RoundPick(): React.ReactElement {
  const step = useSettings((s) => s.roundStep);
  const dir = useSettings((s) => s.roundDir);
  const who = useSettings((s) => s.roundWho);
  const setRound = useSettings((s) => s.setRound);
  return (
    <label className="nx-calc__round">
      <em>округлить</em>
      <select
        value={String(step)}
        aria-label="округлить сумму"
        title={ROUND_TITLE[step]}
        onChange={(event) => {
          const next = Number(event.target.value);
          if (isRoundStep(next)) setRound(next, dir, who);
        }}
      >
        {ROUND_STEPS.map((n) => (
          <option key={n} value={String(n)} title={ROUND_TITLE[n]}>
            {n}
          </option>
        ))}
      </select>
    </label>
  );
}

export function Calc({
  row,
  meta,
  face,
  onFloat,
  stale = false,
  quietHint = false,
  keepRow = false,
}: {
  row: FeedRow;
  meta: Meta;
  face: 'pad' | 'bar' | 'shelf' | 'desk';
  onFloat?: () => void;
  stale?: boolean;
  /** Только слово «нулевые вилки», без порогов контор и «как читать». */
  quietHint?: boolean;
  /** Колл: не пересчитывать банк стола ученика поверх плана куратора. */
  keepRow?: boolean;
}): React.ReactElement {
  const stakeLock = useScreen((s) => s.stakeLock);
  const setStakeLock = useScreen((s) => s.setStakeLock);
  const oddsLock = useScreen((s) => s.oddsLock);
  const setOddsQuote = useScreen((s) => s.setOddsQuote);
  const feeLock = useScreen((s) => s.feeLock);
  const setFeeQuote = useScreen((s) => s.setFeeQuote);
  const curLock = useScreen((s) => s.curLock);
  const setCurQuote = useScreen((s) => s.setCurQuote);
  const freeLock = useScreen((s) => s.freeLock);
  const setFreeLeg = useScreen((s) => s.setFreeLeg);
  const handLock = useScreen((s) => s.handLock);
  const setHandOdds = useScreen((s) => s.setHandOdds);
  const bank = useSettings((s) => s.bank);
  const fx = useSettings((s) => s.fx);
  const caps = useSettings((s) => s.caps);
  const capsCur = useSettings((s) => s.capsCur);
  const couponCaps = useSettings((s) => s.couponCaps);
  const freebets = useSettings((s) => s.freebets);
  const freebetsCur = useSettings((s) => s.freebetsCur);
  const attempts = useSettings((s) => s.attempts);
  const roundStep = useSettings((s) => s.roundStep);
  const roundDir = useSettings((s) => s.roundDir);
  const roundWho = useSettings((s) => s.roundWho);
  const exchangeStake = useSettings((s) => s.exchangeStake);
  const setExchangeStake = useSettings((s) => s.setExchangeStake);
  const unit = useShow((s) => s.unit);
  const pilotCalc = isPilot({ id: useSession((s) => s.me.id), username: useSession((s) => s.me.username) });
  const held = useHeldCalcRow(row.id);
  const view = keepRow ? row : (held.row ?? row);
  stale = keepRow ? stale : stale || held.gone;
  const pinned = useClips().includes(view.id);
  const card = rowWithLock(view, {
    bank,
    fx,
    caps,
    capsCur,
    couponCaps,
    freebets,
    freebetsCur,
    tempo: tempoOf(attempts),
    roundStep,
    roundDir,
    roundWho,
    lock: stakeLock ?? undefined,
    odds: oddsLock ?? undefined,
    fees: feeLock ?? undefined,
    curs: curLock ?? undefined,
    free: freeLock ?? undefined,
    hand: handLock ?? undefined,
    hold: true,
  });
  const seats = useScreen((s) => s.seatLock[row.id]);
  const legs = holdLegs(card.row.legs, seats);
  const withRub = hasRubBook(legs, meta);
  const stakeAs = (leg: RowLeg): ExchangeStake =>
    lockAsOf(stakeLock?.forkId === row.id ? stakeLock : null, leg.source, exchangeStake);
  const flipShareUnit = (leg: RowLeg): void => {
    const from = stakeAs(leg);
    const to: ExchangeStake = from === 'usd' ? 'share' : 'usd';
    setExchangeStake(to);
    if (stakeLock?.forkId === row.id && stakeLock.source === leg.source) {
      setStakeLock(
        stakeLockBody(
          row.id,
          leg.source,
          convertStakeForm(stakeLock.form, from, to, leg.cents),
          leg.unit,
          to,
        ),
      );
    }
  };
  const asUsd = unit !== 'rub' && fx > 0;
  const gain = profitText(card.row.profit, card.row.roiPct, fx, unit);
  const minus = card.row.profit < 0;
  const [totalHold, setTotalHold] = useState<string | null>(null);
  const [asPct, setAsPct] = useState<Record<string, boolean>>({});
  const [showCaps, setShowCaps] = useState(false);
  const [legCaps, setLegCaps] = useState<Record<string, string>>({});
  const [totalCap, setTotalCap] = useState('');
  const caller = useSession((s) => s.me.caller);
  const callSend = useDeskCall((s) => s.sendCalc);
  const callBusy = useDeskCall((s) => s.sending);
  const callNote = useDeskCall((s) => s.tapeNote);

  const tapeFork = useScreen((s) => s.forks.get(row.id) ?? s.hiddenForks.get(row.id)?.fork)
    ?? row.row.fork;
  const promiseKey = liveHoldKey(
    tapeFork,
    `${stakeLock?.forkId === row.id ? stakeLock.form : ''}|${
      oddsLock?.forkId === row.id ? JSON.stringify(oddsLock.by) : ''
    }`,
  );
  useEffect(() => {
    return act.holdPromises(row.id, legs);
  }, [row.id, promiseKey]);

  useEffect(() => {
    const total = card.row.staked > 0 ? card.row.staked : bank;
    useEntry.getState().warm(row.id, total, fx, placeMark(row.row.fork, total, fx));
  }, [row.id, card.row.staked, fx, bank]);

  useEffect(() => {
    setTotalHold(null);
  }, [row.id]);

  const applyTotal = (form: string): void => {
    const n = parseAmount(form);
    if (n === null) return;
    const totalRub = asUsd ? n * fx : n;
    if (!(totalRub > 0) || !(fx > 0 || !asUsd)) return;
    const parts = stakes(String(totalRub), legs.map((leg) => leg.odds));
    const anchor = moneyAnchor(legs);
    if (anchor === undefined) return;
    const idx = legs.indexOf(anchor);
    const part = parts[idx];
    if (part === undefined || idx < 0) return;
    const as = anchor.unit === 'share' ? stakeAs(anchor) : undefined;
    setStakeLock(
      stakeLockBody(row.id, anchor.source, lockForm(part.toNumber(), anchor, fx, meta, as), anchor.unit, as),
    );
  };

  const evenOut = (): void => {
    const form = totalHold ?? shownTotal(card.row.staked, fx, asUsd);
    setTotalHold(form);
    applyTotal(form);
  };

  const capToRub = (form: string, leg: RowLeg): Decimal | null => {
    const n = parseAmount(form);
    if (n === null) return null;
    if (leg.unit === 'share') {
      if (!(fx > 0)) return null;
      if (stakeAs(leg) === 'share') {
        const price = (leg.cents ?? 0) / 100;
        if (!(price > 0)) return null;
        return new Decimal(n).times(price).times(fx);
      }
      return new Decimal(n).times(fx);
    }
    if (isRubStake(leg, meta) || !(fx > 0)) return new Decimal(n);
    return new Decimal(n).times(fx);
  };

  const applyCaps = (nextLeg: Record<string, string>, nextTotal: string): void => {
    const odds = legs.map((leg) => quoteOf(leg, row.id, oddsLock, handOdds, hasHand).form);
    const fromTotal = parseAmount(nextTotal);
    let bank =
      fromTotal !== null
        ? new Decimal(asUsd ? fromTotal * fx : fromTotal)
        : new Decimal(card.row.staked);
    legs.forEach((leg, index) => {
      const raw = nextLeg[leg.source];
      if (raw === undefined || raw.trim() === '') return;
      const maxRub = capToRub(raw, leg);
      const odd = odds[index];
      if (maxRub === null || odd === undefined) return;
      const capBank = maxRub.times(impliedSum(odds)).times(odd);
      if (capBank.lt(bank)) bank = capBank;
    });
    if (!bank.gt(0)) return;
    applyTotal(shownTotal(Number(bank.toFixed(2)), fx, asUsd));
  };

  const oddsKey = legs.map((leg) => leg.odds).join('/');
  useEffect(() => {
    if (totalHold === null) return;
    applyTotal(totalHold);
    // Якорь от набитой суммы: кэф уехал — разкладка едет, сумма та же.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oddsKey, totalHold]);

  const lockOdds = (leg: RowLeg, form: string): void => {
    if (leg.source === HAND_SOURCE) {
      setHandOdds(row.id, form);
      return;
    }
    const shown =
      shareQuote(leg) && lookLikeShareCents(Number(form)) ? oddsFromShareLock(form) : form;
    if (shareQuote(leg)) {
      const cents = centsFromOdds(shown);
      if (cents === null) return;
      setOddsQuote(row.id, leg.source, cents);
      return;
    }
    setOddsQuote(row.id, leg.source, shown);
  };
  const unlockOdds = (leg: RowLeg): void => {
    if (leg.source === HAND_SOURCE) setHandOdds(row.id, null);
    else setOddsQuote(row.id, leg.source, null);
  };
  const lockStake = (leg: RowLeg, form: string): void => {
    setTotalHold(null);
    const as = leg.unit === 'share' ? stakeAs(leg) : undefined;
    setStakeLock(stakeLockBody(row.id, leg.source, form, leg.unit, as));
  };
  const unlockStake = (leg: RowLeg): void => {
    if (stakeLock?.forkId === row.id && stakeLock.source === leg.source) setStakeLock(null);
  };
  const goPick = (leg: RowLeg): void => {
    const dest = rememberHref(row.id, leg.source, leg.side, act.urlOf(row.id, leg));
    if (dest === null) return;
    if (act.searchDoor(leg.source, dest)) void copy(act.matchQuery(row));
    if (act.urlOf(row.id, leg) !== null) act.openBook(row.id, leg);
    else act.openTab(dest, leg.source);
  };

  const totalForm = totalHold ?? shownTotal(card.row.staked, fx, asUsd);
  const liveCount = (row.row.fork.legs ?? []).length;
  const canHand = liveCount === 2;
  const hasHand = handLock?.forkId === row.id;
  const handOdds = hasHand ? handLock?.odds : undefined;

  const totalField = (
    <AmountEdit
      form={totalForm}
      locked={totalHold !== null || stakeLock?.forkId === row.id}
      armed={false}
      live
      onLock={(form) => {
        setTotalHold(form);
        applyTotal(form);
      }}
      onUnlock={() => {
        setTotalHold(null);
        setStakeLock(null);
      }}
    >
      <span>{totalForm}</span>
    </AmountEdit>
  );

  const toggleHand = (): void => {
    if (hasHand) {
      setHandOdds(row.id, null);
      if (stakeLock?.forkId === row.id && stakeLock.source === HAND_SOURCE) {
        setStakeLock(null);
      }
      if (freeLock?.forkId === row.id && freeLock.source === HAND_SOURCE) {
        setFreeLeg(row.id, null);
      }
      setFeeQuote(row.id, HAND_SOURCE, null);
      return;
    }
    setHandOdds(row.id, '2.000');
  };

  if (face === 'shelf') {
    return (
      <div
        className={`nx-calc nx-calc--shelf${stale ? ' stale' : ''}${legs.length > 2 ? ' trio' : ''}`}
      >
        <i className="nx-calc__skip" aria-hidden="true" />
        {legs.map((leg) => {
          const q = quoteOf(leg, row.id, oddsLock, handOdds, hasHand);
          const stakeOn = stakeLock?.forkId === row.id && stakeLock.source === leg.source;
          const edited = editAmount(leg, fx, meta, leg.unit === 'share' ? stakeAs(leg) : undefined);
          return (
            <div key={`${leg.source}:${leg.side}`} className="nx-calc__slot">
              <LiveTick
                on={!q.locked}
                onToggle={() => (q.locked ? unlockOdds(leg) : lockOdds(leg, q.form))}
              />
              <SightDot forkId={row.id} source={leg.source} url={act.urlOf(row.id, leg)} nowRoiPct={card.row.roiPct} />
              <OddsEdit
                kind="odds"
                form={q.form}
                locked={q.locked}
                armed={false}
                live
                onLock={(form) => lockOdds(leg, form)}
                onUnlock={() => unlockOdds(leg)}
              >
                <span>{q.form}</span>
              </OddsEdit>
              <AmountEdit
                form={edited}
                locked={stakeOn}
                armed={false}
                live
                onLock={(form) => lockStake(leg, form)}
                onUnlock={() => unlockStake(leg)}
              >
                <span>{edited}</span>
              </AmountEdit>
            </div>
          );
        })}
        <span className={`nx-calc__tail${minus ? ' lose' : ''}`} title={minus ? 'вилка в минусе' : undefined}>{gain}</span>
        <ZeroHint row={card.row} meta={meta} stale={stale} quiet={pilotCalc} />
      </div>
    );
  }

  if (face === 'bar') {
    const unitMark = asUsd ? '$' : '₽';
    return (
      <div className={`nx-calc nx-calc--bar${stale ? ' stale' : ''}`}>
        {legs.map((leg, index) => {
          const q = quoteOf(leg, row.id, oddsLock, handOdds, hasHand);
          const stakeOn = stakeLock?.forkId === row.id && stakeLock.source === leg.source;
          const copied = formOf(leg, fx, meta);
          const as = leg.unit === 'share' ? stakeAs(leg) : undefined;
          const edited = editAmount(leg, fx, meta, as);
          const first = index === 0;
          const last = index === legs.length - 1;
          const pctOn = asPct[leg.source] === true;
          const shown = pctOn ? impliedOf(q.form) : q.form;
          return (
            <div key={`${leg.source}:${leg.side}`} className="nx-calc__line">
              <span className="nx-calc__say">
                <LiveTick
                  on={!q.locked}
                  onToggle={() => (q.locked ? unlockOdds(leg) : lockOdds(leg, q.form))}
                />
                <OddsEdit
                  kind="odds"
                  form={shown}
                  locked={q.locked}
                  armed={false}
                  live
                  onLock={(form) => {
                    if (!pctOn) {
                      lockOdds(leg, form);
                      return;
                    }
                    const next = oddsFromImplied(form);
                    if (next !== null) lockOdds(leg, next);
                  }}
                  onUnlock={() => unlockOdds(leg)}
                >
                  <span>{shown}</span>
                </OddsEdit>
                <button
                  type="button"
                  className="nx-calc__face"
                  aria-label={pctOn ? 'сейчас процент, нажать — кэф' : 'сейчас кэф, нажать — процент'}
                  aria-pressed={pctOn}
                  onClick={() =>
                    setAsPct((prev) => ({ ...prev, [leg.source]: !pctOn }))
                  }
                >
                  {pctOn ? '%' : 'кэф'}
                </button>
                <i>на</i>
                <AmountEdit
                  form={edited}
                  locked={stakeOn}
                  armed={false}
                  live
                  onLock={(form) => lockStake(leg, form)}
                  onUnlock={() => unlockStake(leg)}
                >
                  <span>{edited}</span>
                </AmountEdit>
                <span className="nx-calc__unit">
                  {as !== undefined ? <ShareUnitEdit as={as} onPick={() => flipShareUnit(leg)} /> : null}
                </span>
                <CopyMark value={copied} />
              </span>
              {first ? (
                <b className={`nx-calc__roi${minus ? ' lose' : ''}`} title={minus ? 'вилка в минусе' : undefined}>{gain}</b>
              ) : null}
              {last && !first ? (
                <span className="nx-calc__all">
                  {totalForm} {unitMark}
                </span>
              ) : null}
            </div>
          );
        })}
        <div className="nx-calc__under">
          <div className="nx-calc__caps">
            <button
              type="button"
              className={`nx-calc__face${showCaps ? ' on' : ''}`}
              aria-pressed={showCaps}
              aria-label="лимит двух ног"
              onClick={() => setShowCaps((on) => !on)}
            >
              лимит
            </button>
            {showCaps
              ? legs.map((leg) => (
                  <label key={leg.source} className="nx-calc__cap">
                    <span>{venueName(meta, leg.source)}</span>
                    <input
                      className="nx-amt__in"
                      inputMode="decimal"
                      aria-label={`лимит ${venueName(meta, leg.source)}`}
                      value={legCaps[leg.source] ?? ''}
                      placeholder="—"
                      onChange={(event) => {
                        const next = { ...legCaps, [leg.source]: event.target.value };
                        setLegCaps(next);
                        applyCaps(next, totalCap);
                      }}
                    />
                  </label>
                ))
              : null}
            {showCaps ? (
              <label className="nx-calc__cap">
                <span>всего</span>
                <input
                  className="nx-amt__in"
                  inputMode="decimal"
                  aria-label="общий лимит на обе ноги"
                  value={totalCap}
                  placeholder="—"
                  onChange={(event) => {
                    setTotalCap(event.target.value);
                    applyCaps(legCaps, event.target.value);
                  }}
                />
              </label>
            ) : null}
          </div>
          <ZeroHint row={card.row} meta={meta} stale={stale} quiet={pilotCalc || quietHint} />
        </div>
      </div>
    );
  }

  if (face === 'desk') {
    return (
      <div className={`nx-calc nx-calc--desk${stale ? ' stale' : ''}${legs.length > 2 ? ' trio' : ''}`}>
        <div className="nx-calc__cols" aria-hidden="true">
          <span>контора</span>
          <span>исход</span>
          <span>кэф</span>
          <span>сумма</span>
          <span>доход</span>
          <i />
        </div>
        {legs.map((leg) => {
          const share = leg.unit === 'share';
          const isHand = leg.source === HAND_SOURCE;
          const q = quoteOf(leg, row.id, oddsLock, handOdds, hasHand);
          const stakeOn = stakeLock?.forkId === row.id && stakeLock.source === leg.source;
          const copied = formOf(leg, fx, meta);
          const as = share ? stakeAs(leg) : undefined;
          const edited = editAmount(leg, fx, meta, as);
          const href = rememberHref(row.id, leg.source, leg.side, act.urlOf(row.id, leg));
          const income = leg.payout - card.row.staked;
          const ex = share || venueKind(meta, leg.source) === 'exchange';
          const feeEntry = feeLock?.forkId === row.id ? feeLock.by[leg.source] : undefined;
          const feeLocked = feeEntry !== undefined;
          const feeForm = feeEntry ?? '';
          const freeOn = leg.freebet === true || (freeLock?.forkId === row.id && freeLock.source === leg.source);
          return (
            <div key={`${leg.source}:${leg.side}`} className="nx-calc__row">
              <span className={`nx-calc__bk${ex ? ' ex' : ''}`}>
                <Brand
                  code={leg.source}
                  name={venueName(meta, leg.source)}
                  size="s"
                  href={href}
                  onOpen={href === null ? undefined : () => goPick(leg)}
                />
              </span>
              <span
                className={href !== null ? 'nx-calc__pick go' : 'nx-calc__pick'}
                role={href !== null ? 'link' : undefined}
                tabIndex={href !== null ? 0 : undefined}
                onClick={
                  href !== null
                    ? (event) => {
                        event.stopPropagation();
                        goPick(leg);
                      }
                    : undefined
                }
                onKeyDown={
                  href !== null
                    ? (event) => {
                        if (event.key !== 'Enter' && event.key !== ' ') return;
                        event.preventDefault();
                        goPick(leg);
                      }
                    : undefined
                }
              >
                {betOn(leg, row.row.fork)}
              </span>
              <span className="nx-calc__odds">
                <LiveTick
                  named
                  on={!q.locked}
                  onToggle={() => (q.locked ? unlockOdds(leg) : lockOdds(leg, q.form))}
                />
                <SightDot forkId={row.id} source={leg.source} url={href} nowRoiPct={card.row.roiPct} />
                <OddsEdit
                  kind="odds"
                  form={q.form}
                  locked={q.locked}
                  armed={false}
                  live
                  onLock={(form) => lockOdds(leg, form)}
                  onUnlock={() => unlockOdds(leg)}
                >
                  <span>{q.form}</span>
                </OddsEdit>
                {q.cents !== null && <i>цена {q.cents}</i>}
              </span>
              <span className="nx-calc__amt">
                <span className="nx-calc__cash">
                  <AmountEdit
                    form={edited}
                    locked={stakeOn}
                    armed={false}
                    live
                    onLock={(form) => lockStake(leg, form)}
                    onUnlock={() => unlockStake(leg)}
                  >
                    <span>{edited}</span>
                  </AmountEdit>
                  {share && as !== undefined ? (
                    <ShareUnitEdit as={as} onPick={() => flipShareUnit(leg)} />
                  ) : !isHand ? (
                    <CurEdit
                      face="mark"
                      cur={isRubStake(leg, meta) ? 'RUB' : 'USD'}
                      venue={catalogCur(leg, meta)}
                      onPick={(next) => setCurQuote(row.id, leg.source, next)}
                    />
                  ) : null}
                </span>
              </span>
              <span className={`nx-calc__inc${income < 0 ? ' lose' : ''}`}>
                <b>{incomeSay(income, asUsd, fx)}</b>
              </span>
              <span className="nx-calc__more">
                <button
                  type="button"
                  className="nx-calc__copy nx-calc__copy--desk"
                  aria-label="скопировать сумму"
                  title={`скопировать ${copied}`}
                  onClick={() => void copy(copied)}
                >
                  <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                    <rect x="5.5" y="5.5" width="8" height="8" rx="1.5" />
                    <path d="M10.5 3.5v-1h-8v8h1" />
                  </svg>
                </button>
                <FeeEdit
                  form={feeForm}
                  locked={feeLocked}
                  onLock={(form) => setFeeQuote(row.id, leg.source, form)}
                  onUnlock={() => setFeeQuote(row.id, leg.source, null)}
                />
                {!leg.lay && !isHand && (
                  <FreeEdit
                    face="ticket"
                    on={freeOn}
                    onToggle={() => {
                      if (freeOn) {
                        setFreeLeg(row.id, null);
                        return;
                      }
                      setFreeLeg(row.id, leg.source);
                      setStakeLock(stakeLockBody(row.id, leg.source, edited, leg.unit, as));
                    }}
                  />
                )}
              </span>
            </div>
          );
        })}
        <div className="nx-calc__foot">
          <div className="nx-calc__foot-sum">
            <span className="nx-calc__plus">
              <em>{minus ? 'вилка в минусе' : 'выйдет'}</em>
              <b className={`nx-calc__roi${minus ? ' lose' : ''}`}>{gain}</b>
            </span>
            <span className="nx-calc__sum">
              <em>всего</em>
              {totalField}
              <u>{asUsd ? '$' : '₽'}</u>
              {withRub && asUsd ? <i>{rub(card.row.staked)} ₽</i> : null}
            </span>
          </div>
          <ZeroHint row={card.row} meta={meta} stale={stale} quiet={pilotCalc} />
          {PRIVATE_MIRROR && <CupisWorkTools id={row.id} />}
          <div className="nx-calc__foot-acts">
            <RoundPick />
            {onFloat !== undefined && (
              <button
                type="button"
                className="nx-calc__tool nx-calc__pop"
                title={
                  canPip()
                    ? 'маленькое окно поверх сайта конторы. telegram и полный экран иногда перебивают'
                    : 'открыть узкое окно рядом с сайтом конторы'
                }
                aria-label="открыть калькулятор в окошке"
                onClick={onFloat}
              >
                в окошко
              </button>
            )}
            {(canHand || hasHand) && (
              <button type="button" className="nx-calc__tool nx-calc__hand" onClick={toggleHand}>
                {hasHand ? 'убрать плечо' : 'ещё плечо'}
              </button>
            )}
            <button
              type="button"
              className="nx-calc__tool"
              aria-label="разложить банк"
              onClick={evenOut}
            >
              разложить банк
            </button>
            <CaptureButton row={row} meta={meta} />
            {caller && (
              <button
                type="button"
                className="nx-calc__tool nx-calc__call"
                disabled={callBusy}
                title={callNote ?? 'позвать учеников: тост и тема стола'}
                onClick={() => void callSend(view)}
              >
                <Bell />
                {callBusy ? 'шлю…' : 'call'}
              </button>
            )}
            {caller && callNote !== null && (
              <span className={`nx-calc__share-note${callNote === 'отправили' ? '' : ' bad'}`} role="status">
                {callNote}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="nx-calc nx-calc--pad">
      <div className="nx-calc__head">
        <span className="nx-calc__plus">
          <em>{minus ? 'вилка в минусе' : 'выйдет'}</em>
          <b className={`nx-calc__roi${minus ? ' lose' : ''}`}>{gain}</b>
        </span>
        <span className="nx-calc__gap" />
        <span className="nx-calc__sum">
          <em>всего</em>
          {totalField}
          <u>{asUsd ? '$' : '₽'}</u>
          {withRub && asUsd ? <i>{rub(card.row.staked)} ₽</i> : null}
        </span>
        {onFloat !== undefined && (
          <button
            type="button"
            className="nx-calc__tool nx-calc__pop"
            title={
              canPip()
                ? 'маленькое окно поверх сайта конторы. telegram и полный экран иногда перебивают'
                : 'открыть узкое окно рядом с сайтом конторы'
            }
            aria-label="открыть калькулятор в окошке"
            onClick={onFloat}
          >
            в окошко
          </button>
        )}
        {!pinned && (
          <button
            type="button"
            className="nx-calc__tool"
            aria-label="в калькулятор"
            onClick={() => toggleClip(row.id)}
          >
            в калькулятор
          </button>
        )}
        {(canHand || hasHand) && (
          <button type="button" className="nx-calc__tool nx-calc__hand" onClick={toggleHand}>
            {hasHand ? 'убрать плечо' : 'ещё плечо'}
          </button>
        )}
        <button
          type="button"
          className="nx-calc__tool"
          aria-label="разложить банк"
          onClick={evenOut}
        >
          разложить банк
        </button>
        {caller && (
          <button
            type="button"
            className="nx-calc__tool"
            disabled={callBusy}
            title={callNote ?? 'позвать учеников: тост и тема стола'}
            onClick={() => void callSend(view)}
          >
            {callBusy ? 'шлю…' : 'call'}
          </button>
        )}
        <CaptureButton row={row} meta={meta} />
        {caller && callNote !== null && (
          <span className={`nx-calc__share-note${callNote === 'отправили' ? '' : ' bad'}`} role="status">
            {callNote}
          </span>
        )}
      </div>
      {legs.map((leg) => {
        const share = leg.unit === 'share';
        const isHand = leg.source === HAND_SOURCE;
        const q = quoteOf(leg, row.id, oddsLock, handOdds, hasHand);
        const stakeOn = stakeLock?.forkId === row.id && stakeLock.source === leg.source;
        const copied = formOf(leg, fx, meta);
        const as = share ? stakeAs(leg) : undefined;
        const edited = editAmount(leg, fx, meta, as);
        const href = rememberHref(row.id, leg.source, leg.side, act.urlOf(row.id, leg));
        const income = leg.payout - card.row.staked;
        const ex = share || venueKind(meta, leg.source) === 'exchange';
        const feeEntry = feeLock?.forkId === row.id ? feeLock.by[leg.source] : undefined;
        const feeLocked = feeEntry !== undefined;
        const feeForm = feeEntry ?? '';
        const freeOn = leg.freebet === true || (freeLock?.forkId === row.id && freeLock.source === leg.source);
        return (
          <div key={`${leg.source}:${leg.side}`} className="nx-calc__card">
            <div className="nx-calc__body">
              <div className="nx-calc__who">
                <span className={`nx-calc__bk${ex ? ' ex' : ''}`}>
                  <Brand
                    code={leg.source}
                    name={venueName(meta, leg.source)}
                    size="s"
                    href={href}
                    onOpen={href === null ? undefined : () => goPick(leg)}
                  />
                </span>
                <span
                  className={href !== null ? 'nx-calc__pick go' : 'nx-calc__pick'}
                  role={href !== null ? 'link' : undefined}
                  tabIndex={href !== null ? 0 : undefined}
                  onClick={
                    href !== null
                      ? (event) => {
                          event.stopPropagation();
                          goPick(leg);
                        }
                      : undefined
                  }
                  onKeyDown={
                    href !== null
                      ? (event) => {
                          if (event.key !== 'Enter' && event.key !== ' ') return;
                          event.preventDefault();
                          goPick(leg);
                        }
                      : undefined
                  }
                >
                  {betOn(leg, row.row.fork)}
                </span>
              </div>
              <span className="nx-calc__odds">
                <span className="nx-calc__odds-head">
                  <u>кэф</u>
                  <LiveTick
                    named
                    on={!q.locked}
                    onToggle={() => (q.locked ? unlockOdds(leg) : lockOdds(leg, q.form))}
                  />
                  <SightDot forkId={row.id} source={leg.source} url={href} nowRoiPct={card.row.roiPct} />
                </span>
                <OddsEdit
                  kind="odds"
                  form={q.form}
                  locked={q.locked}
                  armed={false}
                  live
                  onLock={(form) => lockOdds(leg, form)}
                  onUnlock={() => unlockOdds(leg)}
                >
                  <span>{q.form}</span>
                </OddsEdit>
                {q.cents !== null && <i>цена {q.cents}</i>}
              </span>
              <span className="nx-calc__amt">
                <u>сумма</u>
                <span className="nx-calc__cash">
                  <AmountEdit
                    form={edited}
                    locked={stakeOn}
                    armed={false}
                    live
                    onLock={(form) => lockStake(leg, form)}
                    onUnlock={() => unlockStake(leg)}
                  >
                    <span>{edited}</span>
                  </AmountEdit>
                  {share && as !== undefined ? (
                    <ShareUnitEdit as={as} onPick={() => flipShareUnit(leg)} />
                  ) : !isHand ? (
                    <CurEdit
                      face="mark"
                      cur={isRubStake(leg, meta) ? 'RUB' : 'USD'}
                      venue={catalogCur(leg, meta)}
                      onPick={(next) => setCurQuote(row.id, leg.source, next)}
                    />
                  ) : null}
                </span>
              </span>
              <span className={`nx-calc__inc${income < 0 ? ' lose' : ''}`}>
                <u>доход</u>
                <b>{incomeSay(income, asUsd, fx)}</b>
              </span>
            </div>
            <div className="nx-calc__tools">
              <button
                type="button"
                className="nx-calc__tool"
                aria-label="скопировать сумму"
                title={`скопировать ${copied}`}
                onClick={() => void copy(copied)}
              >
                скопировать
              </button>
              <FeeEdit
                form={feeForm}
                locked={feeLocked}
                onLock={(form) => setFeeQuote(row.id, leg.source, form)}
                onUnlock={() => setFeeQuote(row.id, leg.source, null)}
              />
              {!leg.lay && !isHand && (
                <FreeEdit
                  on={freeOn}
                  onToggle={() => {
                    if (freeOn) {
                      setFreeLeg(row.id, null);
                      return;
                    }
                    setFreeLeg(row.id, leg.source);
                    setStakeLock(stakeLockBody(row.id, leg.source, edited, leg.unit, as));
                  }}
                />
              )}
              <button
                type="button"
                className={`nx-calc__tool${stakeOn ? ' on' : ''}`}
                aria-pressed={stakeOn}
                aria-label={stakeOn ? 'сумма зафиксирована' : 'не менять сумму'}
                title={
                  stakeOn
                    ? 'эта сумма железка, второе плечо едет под неё. нажать — снова от банка'
                    : 'зафиксировать эту сумму: второе плечо само подстроится'
                }
                onClick={() => (stakeOn ? unlockStake(leg) : lockStake(leg, edited))}
              >
                {stakeOn ? 'зафиксировал' : 'не менять'}
              </button>
            </div>
          </div>
        );
      })}
      <ZeroHint row={card.row} meta={meta} stale={stale} quiet={pilotCalc} />
    </div>
  );
}

/** Узкое окно калькулятора: та же вилка, без плиток и «открыть обе». */
export function CalcFloat({
  id,
  frame,
}: {
  id: string;
  frame?: Window;
}): React.ReactElement {
  const meta = useScreen((s) => s.meta);
  const { calcSkin } = useDeskLayout();
  const { row, gone, reconnecting } = useHeldCalcRow(id);

  useEffect(() => {
    const parts = row?.row.fork.legs?.[0]?.participants ?? [];
    const title = parts.length > 0 ? `${parts.join(' — ')} · калькулятор` : 'калькулятор';
    (frame?.document ?? document).title = title;
  }, [row, frame]);

  useEffect(() => {
    if (frame === undefined) return;
    const wide = calcSkin === 'fork' ? 450 : 200;
    try {
      frame.resizeTo(340, wide);
    } catch {
      // PiP и чужой origin размер не отдают.
    }
    const host = frame.document.getElementById('fork-root');
    if (host !== null) host.className = `nx-root ${calcSkin === 'fork' ? 'nx-root--fork' : 'nx-root--bar'}`;
  }, [calcSkin, frame]);

  if (calcSkin === 'fork') return <CalcFork row={row} stale={gone} />;

  if (row !== null) return <Calc row={row} meta={meta} face="bar" stale={gone} quietHint />;

  return (
    <p className="nx-calc__wait">
      {reconnecting ? 'подключаемся к движку…' : 'нет чисел этой вилки'}
    </p>
  );
}
